import React from 'react';
import { Row, Col } from 'react-bootstrap';
import { debounce, cloneDeep } from 'lodash';
import { useParams } from 'react-router-dom';
import { Label } from '@progress/kendo-react-labels';
import { NumericTextBox, Checkbox } from '@progress/kendo-react-inputs';

import {
  GetTaskContentById,
  UpdateGameDocState,
  UpdateTaskContentAsync
} from '../../../../utils/game-document';
import {
  TARMiniGame,
  TaskContent
} from '../../../../types/game-document/entities/task-content';
import { ARMiniGameContent } from '../../../../constants/ar-mini-game';
import { GameDocumentContext } from '../../../../contexts/game-document';
import DurationPicker from '../../../../components/forms/duration-picker';

export const ARGameSetting: React.FC = () => {
  const { taskContentId } = useParams();
  const [state, setState] = React.useContext(GameDocumentContext);

  const taskContent = GetTaskContentById(
    state.gameDocument,
    taskContentId ?? ''
  );
  const arMiniGameKey = (taskContent?.ARMiniGame === ('Where in the World?' as any)
    ? 'Where in the World? - Hard'
    : taskContent?.ARMiniGame) as TARMiniGame;
  const ARGameDefaultSetting =
    ARMiniGameContent[taskContent?.ARMiniGame as TARMiniGame].setting;
  const ARGameSetting = taskContent?.ARMiniGameSetting ?? {
    score: ARGameDefaultSetting.score,
    ...((taskContent?.ARMiniGame as TARMiniGame) === 'Meerkats Moves' && {
      manualScoring: {
        min: 0,
        max: 0
      }
    }),
    ...(ARGameDefaultSetting.timer !== undefined && {
      scoringReduction: {
        maximumTimeLimit: ARGameDefaultSetting.timer,
        scoreReduction: undefined,
        scoreReductionInterval: undefined
      }
    }),
    ...(ARGameDefaultSetting.mode !== undefined && {
      mode: ARGameDefaultSetting.mode
    })
  };

  const updateTaskContent = (updatedResult: TaskContent) =>
    UpdateTaskContentAsync(
      state.gameDocument!,
      taskContentId!,
      updatedResult,
      false
    ).then(async (response) =>
      setState((prev) => UpdateGameDocState(prev, response))
    );

  const debouncedUpdate = React.useRef(
    debounce(
      (updatedResult: TaskContent) => {
        updateTaskContent(updatedResult);
      },
      500,
      { leading: false, trailing: true }
    )
  ).current;

  const handleChangeCheckbox = (field: string, value: boolean) => {
    const result = cloneDeep(taskContent!);
    if (!result.ARMiniGameSetting) result.ARMiniGameSetting = ARGameSetting;

    if (field === 'manualScoring') {
      if (value) {
        result.ARMiniGameSetting.manualScoring = {
          max: 0,
          min: 0
        };
        result.ARMiniGameSetting.score = 0;
        delete result.ARMiniGameSetting.scoringReduction?.scoreReduction;
        delete result.ARMiniGameSetting.scoringReduction
          ?.scoreReductionInterval;
      } else {
        result.ARMiniGameSetting.score = ARGameDefaultSetting.score;
        delete result.ARMiniGameSetting.manualScoring;
      }
    }

    if (field === 'timeLimit') {
      if (value) {
        result.ARMiniGameSetting.scoringReduction = {
          maximumTimeLimit: 0
        };
      } else {
        delete result.ARMiniGameSetting.scoringReduction;
      }
    }

    if (field === 'pointReduction') {
      if (value) {
        result.ARMiniGameSetting.scoringReduction = {
          ...result.ARMiniGameSetting.scoringReduction,
          scoreReduction: 0,
          scoreReductionInterval: 0
        };
      } else {
        delete result.ARMiniGameSetting.scoringReduction?.scoreReduction;
        delete result.ARMiniGameSetting.scoringReduction
          ?.scoreReductionInterval;
      }
    }

    updateTaskContent(result);
  };

  const handleChangeValue = (field: string, value: number) => {
    const result = cloneDeep(taskContent!);
    if (!result.ARMiniGameSetting) result.ARMiniGameSetting = ARGameSetting;

    if (field === 'score') result.ARMiniGameSetting.score = value;

    if (result.ARMiniGameSetting.manualScoring) {
      if (field === 'manualMin')
        result.ARMiniGameSetting.manualScoring.min = value;
      if (field === 'manualMax')
        result.ARMiniGameSetting.manualScoring.max = value;
    }

    if (field === 'timeLimit') {
      result.ARMiniGameSetting.scoringReduction = {
        ...result.ARMiniGameSetting.scoringReduction,
        maximumTimeLimit: value
      };
    }

    if (field === 'pointReduction') {
      result.ARMiniGameSetting.scoringReduction = {
        ...result.ARMiniGameSetting.scoringReduction,
        scoreReduction: value
      };
    }

    if (field === 'timeIncrements') {
      result.ARMiniGameSetting.scoringReduction = {
        ...result.ARMiniGameSetting.scoringReduction,
        scoreReductionInterval: value
      };
    }

    field.includes('time')
      ? updateTaskContent(result)
      : // use debounce to handle performance when updating typed value
        debouncedUpdate(result);
  };

  React.useEffect(() => {
    return () => {
      debouncedUpdate.cancel();
    };
  }, []);

  return (
    <Row>
      <Col md={6}>
        <Row>
          <Col sm={12} md={6} lg={3} xl={3}>
            <div className={'d-flex justify-content-center flex-column w-100'}>
              <div className={'d-flex'}>
                <span className={'fw-bold'}>Score</span>
              </div>
              <div>
                <NumericTextBox
                  min={0}
                  value={ARGameSetting.score}
                  disabled={
                    taskContent?.ARMiniGame === 'Meerkats Moves' ||
                    ARGameSetting.manualScoring !== undefined
                  }
                  onChange={(e) => handleChangeValue('score', e.value ?? 0)}
                />
              </div>
            </div>
          </Col>
        </Row>
      </Col>

      <Col md={6}>
        <Row>
          <Col>
            <Checkbox
              label={'Enable manual scoring'}
              checked={ARGameSetting.manualScoring !== undefined}
              disabled={taskContent?.ARMiniGame === 'Meerkats Moves'}
              onChange={(e) => handleChangeCheckbox('manualScoring', e.value)}
            />
          </Col>
        </Row>

        {ARGameSetting.manualScoring !== undefined && (
          <Row className={'d-flex align-items-center mt-2 p-1'}>
            <Col sm={'12'} md={'12'} lg={'4'} xl={'4'}>
              <Label>Suggested score range</Label>
            </Col>
            <Col
              sm={'12'}
              md={'12'}
              lg={'8'}
              xl={'8'}
              className={'d-flex align-items-center'}>
              <Label className={'mr-2'}>From</Label>
              <NumericTextBox
                className={'mr-2'}
                value={ARGameSetting.manualScoring?.min ?? 0}
                onChange={(e) => handleChangeValue('manualMin', e.value ?? 0)}
              />

              <Label className={'mr-2'}>To</Label>
              <NumericTextBox
                value={ARGameSetting.manualScoring?.max ?? 0}
                onChange={(e) => handleChangeValue('manualMax', e.value ?? 0)}
              />
            </Col>
          </Row>
        )}

        <hr />

        <Row className={'d-flex justify-content-between'}>
          <Col sm={'12'} md={'6'} lg={'6'} xl={'6'}>
            <Checkbox
              label={'Time Limit'}
              id={'checkbox-scoring-reduction'}
              disabled={ARGameDefaultSetting.timer === undefined}
              checked={
                ARGameSetting.scoringReduction?.maximumTimeLimit !== undefined
              }
              onChange={(e) => handleChangeCheckbox('timeLimit', e.value)}
            />
          </Col>
        </Row>

        {ARGameSetting.scoringReduction?.maximumTimeLimit !== undefined && (
          <>
            <Row className={'d-flex align-items-center mt-2 p-1'}>
              <Col sm={'12'} md={'12'} lg={'12'} xl={'12'}>
                <Row>
                  <Col sm={'6'} md={'6'} lg={'6'} xl={'6'}>
                    <span className={'mr-2'}>Time Limit</span>
                  </Col>
                  <Col sm={'6'} md={'6'} lg={'6'} xl={'6'}>
                    <DurationPicker
                      value={
                        ARGameSetting.scoringReduction?.maximumTimeLimit ?? 0
                      }
                      onChange={(newSecond) =>
                        handleChangeValue('timeLimit', newSecond)
                      }
                      className={'mr-2'}
                      width={170}
                      format="HH:mm:ss"
                    />
                  </Col>
                </Row>
              </Col>
            </Row>
          </>
        )}
      </Col>
    </Row>
  );
};
